import { InfoTooltip, Text } from '@/components/DesignSystem';
import { MetricsEndpoint } from '@api/endpoints';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PromotionStatusChip } from '@/pages/Promotions/PromotionStatusChip';
import { PromotionStatus } from '@/utilities/api/PromotionUtilities';
import {
  ColumnKey,
  countPromotionStatuses,
  metricColumns,
  promotionLabel,
} from './metricColumns';
import { periodLabel } from './periodLabel';

type FundingMetrics = MetricsEndpoint.GetPointsMetrics.FundingMetrics;
type Metrics = MetricsEndpoint.GetPointsMetrics.PointsMetrics;
type Row = MetricsEndpoint.GetPointsMetrics.PointsMetricsRow;

const HeaderCell = ({ align, column }: { align?: 'right', column: ColumnKey }) => {
  const { t } = useTranslation();
  return (
    <TableCell align={align}>
      <Stack
        alignItems="center"
        direction="row"
        gap="4px"
        justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
      >
        {t($ => $.pointsMetrics.columns[column])}
        <InfoTooltip placement="top">
          {t($ => $.pointsMetrics.columnTooltips[column])}
        </InfoTooltip>
      </Stack>
    </TableCell>
  );
};

const MetricHeaderCells = () => (
  <>
    {metricColumns.map(({ column, key }) => (
      <HeaderCell align="right" column={column} key={key} />
    ))}
  </>
);

const MetricCells = ({ metrics }: { metrics: Metrics }) => {
  const { t } = useTranslation();
  return (
    <>
      {metricColumns.map(({ key }) => (
        <TableCell align="right" key={key}>
          {key === 'pointsRedemptionRate'
            ? t($ => $.pointsMetrics.values.rate, { value: metrics[key] * 100 })
            : t($ => $.pointsMetrics.values.points, { value: metrics[key] })}
        </TableCell>
      ))}
    </>
  );
};

/** Retailer-funded, manufacturer-funded and total rows for one set of totals. */
export const FundingTable = ({ funding, testID }: { funding: FundingMetrics, testID: string }) => {
  const { t } = useTranslation();
  const rows = [
    { label: t($ => $.pointsMetrics.funding.retailer), metrics: funding.retailer },
    { label: t($ => $.pointsMetrics.funding.manufacturer), metrics: funding.manufacturer },
    { label: t($ => $.pointsMetrics.funding.total), metrics: funding.total },
  ];
  return (
    <TableContainer component={Paper} data-testid={testID}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <HeaderCell column="funding" />
            <MetricHeaderCells />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell>
                <Text category="body-medium">{row.label}</Text>
              </TableCell>
              <MetricCells metrics={row.metrics} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const getRowKey = (row: Row) => `${row.retailerId}:${row.period.programId ?? 'outstanding'}`;

/** One row per retailer and period, expandable to its funding split and manufacturers. */
export const BreakdownTable = ({ rows }: { rows: Row[] }) => {
  const { t } = useTranslation();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const indentedCell = (label: string, depth: number, detail?: ReactNode) => (
    <TableCell colSpan={3} sx={{ pl: 2 + depth * 3 }}>
      <Stack alignItems="center" direction="row" flexWrap="wrap" gap="8px">
        <Text category="body-medium">{label}</Text>
        {detail}
      </Stack>
    </TableCell>
  );

  const getPromotionDetail = (promotion: Row['promotions'][number]) => (
    !!promotion.startDate && !!promotion.endDate && (
      <>
        <PromotionStatusChip
          promotion={{ endDate: promotion.endDate, startDate: promotion.startDate }}
        />
        <Text category="body-medium" color="secondary">
          {t($ => $.pointsMetrics.promotions.awarded, { points: promotion.pointsAwarded ?? 0 })}
        </Text>
      </>
    )
  );

  const getWalletDetail = (wallet: Row['wallets'][number]) => {
    const counts = countPromotionStatuses(wallet.promotionPeriods);
    return (
      <Text category="body-medium" color="secondary">
        {t($ => $.pointsMetrics.wallets.promotionCounts, {
          expired: counts[PromotionStatus.EXPIRED],
          future: counts[PromotionStatus.FUTURE],
          running: counts[PromotionStatus.RUNNING],
        })}
      </Text>
    );
  };

  return (
    <TableContainer
      component={Paper}
      data-testid="points-metrics-breakdown"
      sx={{ overflow: 'hidden' }}
    >
      {/* Scrolls inside the padding, so the frozen header sits flush with no rows above it. */}
      <Box
        data-testid="points-metrics-breakdown-scroll"
        sx={{ maxHeight: '70vh', overflow: 'auto' }}
      >
        <Table size="small" stickyHeader>
          <TableHead
            sx={{
              // An opaque match for the theme's translucent header grey, so rows don't show through.
              '& .MuiTableCell-root.MuiTableCell-head': {
                backgroundColor: theme => theme.palette.grey[100],
              },
            }}
          >
          <TableRow>
            <TableCell padding="checkbox" />
            <HeaderCell column="retailer" />
            <HeaderCell column="period" />
            <MetricHeaderCells />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const key = getRowKey(row);
            const isExpanded = expandedKeys.has(key);
            return (
              <Fragment key={key}>
                <TableRow hover>
                  <TableCell padding="checkbox">
                    <IconButton
                      aria-label={t($ => $.pointsMetrics.toggleBreakdown)}
                      onClick={() => toggle(key)}
                      size="small"
                    >
                      {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{row.retailerName}</TableCell>
                  <TableCell>
                    {periodLabel(row.period.type === 'outstanding' ? null : row.period, t)}
                  </TableCell>
                  <MetricCells metrics={row.metrics.total} />
                </TableRow>
                {isExpanded && (
                  <>
                    <TableRow>
                      {indentedCell(t($ => $.pointsMetrics.funding.retailer), 1)}
                      <MetricCells metrics={row.metrics.retailer} />
                    </TableRow>
                    <TableRow>
                      {indentedCell(t($ => $.pointsMetrics.funding.manufacturer), 1)}
                      <MetricCells metrics={row.metrics.manufacturer} />
                    </TableRow>
                    {row.manufacturers.map((manufacturer) => (
                      <TableRow key={manufacturer.manufacturerId}>
                        {indentedCell(
                          manufacturer.manufacturerName
                            ?? t($ => $.pointsMetrics.unknownManufacturer),
                          2,
                        )}
                        <MetricCells metrics={manufacturer} />
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3 + metricColumns.length} sx={{ pl: 5 }}>
                        <Text category="label-medium">
                          {t($ => $.pointsMetrics.promotions.heading)}
                        </Text>
                      </TableCell>
                    </TableRow>
                    {row.promotions.map((promotion) => (
                      <TableRow
                        data-testid="points-metrics-promotion-row"
                        key={promotion.promotionId ?? 'program'}
                      >
                        {indentedCell(
                          promotionLabel(promotion, t),
                          2,
                          getPromotionDetail(promotion),
                        )}
                        <MetricCells metrics={promotion} />
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3 + metricColumns.length} sx={{ pl: 5 }}>
                        <Text category="label-medium">
                          {t($ => $.pointsMetrics.wallets.heading)}
                        </Text>
                      </TableCell>
                    </TableRow>
                    {row.wallets.map((wallet) => (
                      <TableRow data-testid="points-metrics-wallet-row" key={wallet.subwalletId}>
                        {indentedCell(
                          wallet.subwalletName ?? t($ => $.pointsMetrics.wallets.unknown),
                          2,
                          getWalletDetail(wallet),
                        )}
                        <MetricCells metrics={wallet} />
                      </TableRow>
                    ))}
                  </>
                )}
              </Fragment>
            );
          })}
        </TableBody>
        </Table>
      </Box>
    </TableContainer>
  );
};
